import * as React from 'react';
import { ElementType, FC, ReactNode } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import MuiSelect, {
  SelectChangeEvent,
  SelectProps,
} from '@mui/material/Select';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';

import { ActiveDot } from '../active-dot/active-dot';
import {
  useWindowedTooltipZIndex,
  withWindowedMenuProps,
} from '../dialog/windowed-tooltip-zindex-context';
import { Icon } from '../icon';
import { useScaledMenuProps } from '../select/use-scaled-menu-props';
import { FormControllerTitle } from '../style/form-controllers';

type Option = {
  id: string | number;
  label?: ReactNode;
  value?: number | string;
  disabled?: boolean;
};

const ALL_OPTIONS_VALUE = -1;

type Props<T> = SelectProps & {
  isLoading?: boolean;
  options?: Option[];
  value?: (number | string)[];
  helperText?: ReactNode;
  MenuItem?: FC<MenuItemProps>;
  sx?: SelectProps['sx'];
  iconComponent?: ElementType;
  withAllOption?: boolean;
  active?: boolean;
  onChange?: (event: SelectChangeEvent<T[]>, child: ReactNode) => void;
};

export const MultiSelect = <T,>({
  required,
  value,
  size,
  name,
  onChange,
  label,
  helperText,
  error,
  fullWidth,
  disabled,
  options,
  sx,
  withAllOption,
  active,
  MenuProps,
}: Props<T>) => {
  const windowedConfig = useWindowedTooltipZIndex();
  const {
    anchorRef,
    measure,
    menuProps: scaledMenuProps,
  } = useScaledMenuProps(MenuProps);

  const optionsByValue = React.useMemo(
    () => keyBy(options || [], 'value'),
    [options]
  );

  const handleChange = (
    event: SelectChangeEvent<T[]>,
    child: React.ReactNode
  ) => {
    if (!withAllOption || !event?.target) {
      onChange && onChange(event, child);
      return;
    }

    const prev = (value || []) as (number | string)[];
    const rawNext = event.target.value as (number | string)[];

    let next = Array.isArray(rawNext) ? [...rawNext] : [rawNext];
    const hasAll = next.includes(ALL_OPTIONS_VALUE);
    const prevHadAll = prev.includes(ALL_OPTIONS_VALUE);

    if (hasAll && next.length > 1) {
      next = prevHadAll
        ? next.filter((v) => v !== ALL_OPTIONS_VALUE)
        : [ALL_OPTIONS_VALUE];
    } else if (!hasAll && prevHadAll) {
      next = next.filter((v) => v !== ALL_OPTIONS_VALUE);
    }

    if (withAllOption && isEmpty(next)) {
      next = [ALL_OPTIONS_VALUE];
    }
    const patchedEvent = {
      ...event,
      target: {
        ...(event.target as any),
        value: next as unknown[] as T[],
      },
    } as SelectChangeEvent<T[]>;
    onChange && onChange(patchedEvent, child);
  };

  const SelectIcon: ElementType = (iconProps: any) => (
    <Icon
      name="KeyboardArrowDown"
      sx={{
        alignItems: 'end',
        justifyContent: 'end',
        display: 'flex',
        ...sx,
      }}
      {...iconProps}
    />
  );

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
    >
      <FormControllerTitle>
        {active ? (
          <Box display="flex" alignItems="center" gap={1}>
            {label}
            <ActiveDot />
          </Box>
        ) : (
          label
        )}
      </FormControllerTitle>
      <MuiSelect
        multiple
        ref={anchorRef}
        onOpen={measure}
        name={name}
        value={(value || []) as any}
        onChange={handleChange}
        sx={sx}
        MenuProps={withWindowedMenuProps(windowedConfig, scaledMenuProps)}
        IconComponent={SelectIcon}
        renderValue={(selected) =>
          map(selected, (v) => optionsByValue[v]?.label ?? v).join(', ')
        }
      >
        {map(options || [], (item) => {
          const isSelected = (value || []).includes(
            item.value as string | number
          );
          return (
            <MenuItem
              key={item.id}
              id={String(item.id)}
              disabled={item.disabled}
              style={{
                fontSize: 'smaller',
                display: 'flex',
                alignItems: 'center',
              }}
              value={item.value as any}
            >
              {isSelected ? (
                <CheckBoxIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon
                  fontSize="small"
                  sx={{ mr: 1 }}
                  color="primary"
                />
              )}
              {item.label}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {error && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};
